import { Modal, ProgressIndicator, Stack, Text } from '@fluentui/react';
import { useStore } from '../context/store';
import { useEffect, useMemo, useState } from 'react';

const getRenderId = () => Math.random();

export const handleRetryResponse = async (apiCallFn: (...args: any[]) => Promise<any>, ...args: any[]): Promise<any> => {
    const response = await apiCallFn(...args);
	if (response?.status === 429) { // 429 Too Many Requests
		const retryAfterString = response.headers.get('retry-after') || 10000;
		const duration = Number(retryAfterString);
		useStore.getState().setRetryBlockDialog(
			<WaitDialog
				// We generate a random key to reset det progress on retries that results in further waits
				key={getRenderId()}
				duration={duration}
			/>,
		);

		await new Promise(r => setTimeout(r, duration));
		return handleRetryResponse(apiCallFn, ...args);
	}
    if (useStore.getState().retryBlockContent) {
        useStore.getState().setRetryBlockDialog(null);
    }
    return response;
};

type ProgressIndicatorProps = {
    duration: number; // Duration in milliseconds
};

const intervalDelay = 100;

// eslint-disable-next-line react-refresh/only-export-components
const ProgressIndicatorByDuration = ({ duration }: ProgressIndicatorProps) => {
    const [percentComplete, setPercentComplete] = useState(0);

    // Calculate how much we should increment on each interval
    const intervalIncrement = useMemo(() => intervalDelay / duration, [duration]);

    useEffect(() => {
        if (percentComplete < 1) {
            const id = setTimeout(() => {
                setPercentComplete(Math.min(percentComplete + intervalIncrement, 1)); // ensure it doesn't exceed 1
            }, intervalDelay);

            return () => {
                clearTimeout(id);
            };
        }
    }, [intervalIncrement, percentComplete]);

    return <ProgressIndicator percentComplete={percentComplete} />;
};

// eslint-disable-next-line react-refresh/only-export-components
const WaitDialog = ({ duration }: ProgressIndicatorProps) => {
    return (
        <Modal isOpen isBlocking>
            <Stack tokens={{ padding: 10, childrenGap: 20 }} styles={{ root: { height: '100%' } }}>
                <Text variant="large">We're at max capacity</Text>
                <Text>Thank you for your patience - we're handling an unusually high volume right now. Please hang in there</Text>
                <ProgressIndicatorByDuration duration={duration} />
            </Stack>
        </Modal>
    );
};
